import React from 'react';
import PropTypes from 'prop-types';
import Table from 'components/Table';
import { formatDate, formatNumber } from 'utils/functions';
import {
  Building, Pencil, PlusIcon, RefreshCw, Trash,
} from 'lucide-react';
import { ROUTE_PATH } from 'routes';
import { useNavigate } from 'react-router-dom';
import { fetchLocal } from 'components/Table/components/TableFunctions';
import { Tag } from 'xland-ui';
import { deleteInvestorAsset, synchronizeInvestorAsset } from 'modules/investmentAssets/repository';
import useDialog from 'components/Dialog/useDialog';
import { InvestmentTypeColors } from '../constants';

function InvestmentTable({
  literals, operations, setEditInvestment, groupBy, loading, refresh, currencySymbol,
}) {
  const navigate = useNavigate();
  const { dialog } = useDialog();

  const handleDelete = async (row) => {
    const confirm = await dialog({
      type: 'confirmDanger', text: literals.questionRemoveCaptableRequest,
    });

    if (confirm) {
      await dialog({
        type: 'loading',
        execute: async () => {
          await deleteInvestorAsset(row.id, { organization: row.organization.id });
          refresh();
        },
      });
    }
  };

  const filters = [
    {
      id: 'operation.status',
      type: 'select',
      title: literals.status,
      options: [
        { id: 'active', name: literals.active },
        { id: 'divested', name: literals.divested },
        { id: 'writtenOff', name: literals.writtenOff },
      ],
    },
    {
      id: 'asset.type',
      type: 'select',
      title: literals.type,
      options: [
        { id: 'company', name: literals.company },
        { id: 'fund', name: literals.fund },
        { id: 'listedCompany', name: literals.listedCompany },
        { id: 'realEstate', name: literals.realEstate },
        { id: 'crypto', name: literals.crypto },
        { id: 'other', name: literals.other },
      ],
    },
  ];

  let columns = [
    {
      field: 'name',
      type: 'avatar',
      preRender: (_, row) => row?.logo,
      title: (v) => v,
    },
    {
      field: 'name',
      label: literals.name,
      sortable: true,
      preRender: (v, row) => (
        <div>
          <div className='fc-highest'>{literals[v] || v}</div>
          {row?.organization?.name && (
            <div className='fc-light fs-sm'>{row?.organization?.name}</div>
          )}
        </div>
      ),
    },
    {
      field: 'type',
      label: literals.type,
      preRender: (type) => (type ? (
        <Tag
          text={literals[type]}
          color={InvestmentTypeColors[type]}
          big
        />
      ) : '-'),
      sortable: true,
      width: '110px',
    },
    {
      field: 'status',
      label: literals.status,
      preRender: (v) => (v ? <Tag text={literals[v]} big /> : '-'),
      width: '80px',
      sortable: true,
    },
    {
      field: 'date',
      label: literals.date,
      preRender: (v) => (v ? formatDate(v, { format: 'short' }) : '-'),
      width: '80px',
      sortable: op => (op?.date ? new Date(op.date).getTime() : null),
    },
    {
      field: 'invested',
      label: 'Invested',
      preRender: (v) => formatNumber(v, '-', { symbol: currencySymbol }),
      width: '90px',
      sortable: true,
    },
    // {
    //   field: 'realized',
    //   label: literals.realized,
    //   preRender: (v) => formatNumber(v, '-', {}),
    //   width: '90px',
    //   sortable: true,
    // },
    // {
    //   field: 'unrealized',
    //   label: literals.unrealized,
    //   preRender: (_, { value, realized }) => (value && realized ? value - realized : '-'),
    //   width: '110px',
    //   sortable: ({ value, realized }) => (value && realized ? value - realized : null),
    // },
    {
      field: 'value',
      label: literals.value,
      preRender: (v) => formatNumber(v, '-', { symbol: currencySymbol }),
      width: '80px',
      sortable: true,
    },
    {
      field: 'moic',
      label: 'MOIC',
      preRender: (_, { value, invested }) => (value && invested
        ? formatNumber(value / invested, '-', { symbol: 'x' })
        : '-'
      ),
      width: '80px',
      sortable: ({ value, invested }) => (value && invested ? value / invested : null),
    },
    {
      type: 'menu',
      buttons: [
        {
          icon: <Pencil />,
          name: literals.edit,
          onClick: ({ asset, organization }) => setEditInvestment({
            ...asset, organization: organization.id,
          }),
          hidden: (_, { organization }) => !organization?.permissions || organization?.permissions < 2,
        },
        {
          icon: <RefreshCw />,
          name: literals.synchronize,
          onClick: async ({ id, organization }) => {
            await dialog({
              type: 'loading',
              execute: async () => {
                await synchronizeInvestorAsset(id, organization.id);
                refresh();
              },
            });
          },
        },
        {
          icon: <Trash />,
          name: literals.delete,
          mode: 'danger',
          onClick: handleDelete,
          hidden: (_, { organization }) => !organization?.permissions || organization?.permissions < 2,
        },
      ],
    },
  ];

  if (groupBy === 'type') {
    columns = columns.filter(column => column.field !== 'type');
  } else if (groupBy === 'status') {
    columns = columns.filter(column => column.field !== 'status');
  }

  const fetchMyInvestments = async (page, size, search, filt, sort) => {
    const otherData = { columns, search: ['asset.name'] };
    return fetchLocal(operations, otherData, page, size, search, filt, sort || '-operation.invested');
  };

  return operations && !loading ? (
    <Table
      className='investment-table'
      filters={filters}
      columns={columns}
      fetch={fetchMyInvestments}
      onClickRow={row => !row.rowDetails && navigate(ROUTE_PATH.setInvestorInvestment(row.id, row.organization.id))}
      forceFetch={operations.count}
      alert={{
        type: 'info',
        text: `${operations.investments} ${literals.investments}`,
        className: 'alert-investments',
      }}
      actionsButtons={2}
      actions={[
        {
          icon: <Building />,
          text: literals.myCompanies,
          color: 'tertiary',
          onClick: () => navigate(ROUTE_PATH.PREHOME),
        },
        {
          icon: <PlusIcon />,
          text: literals.addInvestment,
          onClick: () => setEditInvestment({ id: '' }),
        },
      ]}
    />
  ) : (
    <Table
      key='loading'
      columns={columns}
      fetch={false}
    />
  );
}

InvestmentTable.propTypes = {
  literals: PropTypes.object.isRequired,
  operations: PropTypes.object.isRequired,
  refresh: PropTypes.func.isRequired,
  setEditInvestment: PropTypes.func.isRequired,
  groupBy: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  currencySymbol: PropTypes.string.isRequired,
};

export default InvestmentTable;
